// v10/src/components/admin/inventory/ByAircraftView.js
//
// Vista "Por aeronave" del Stock de repuestos (A8) — Tarea 1 (lectura).
// Selector de aeronave activa de la org + listado de productos compatibles
// (incluye universales). El backend resuelve la mezcla con
// GET /api/inventory-items?aircraft_id={uuid}.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Plane, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/apiService';
import inventoryService from '../../../services/inventoryService';
import { POLLING_INTERVALS } from '../../../config/realtime';
import InventoryFiltersBar from './InventoryFiltersBar';
import InventoryItemsList from './InventoryItemsList';

const INITIAL_FILTERS = {
  search: '',
  category: '',
  tracking_mode: '',
  low_stock: false,
};

const buildParams = (filters, aircraftId) => {
  const out = { aircraft_id: aircraftId };
  if (filters?.search)        out.search        = filters.search;
  if (filters?.category)      out.category      = filters.category;
  if (filters?.tracking_mode) out.tracking_mode = filters.tracking_mode;
  if (filters?.low_stock)     out.low_stock     = 'true';
  return out;
};

const ByAircraftView = ({
  darkMode,
  locale = 'es-AR',
  isActive = true,
  selectedAircraftId,
  onAircraftChange,
  onItemAction,
  canEdit = true,
  canDelete = true,
  refreshKey = 0,
}) => {
  const { t } = useTranslation('admin');

  const [aircraftList,    setAircraftList]    = useState([]);
  const [aircraftLoading, setAircraftLoading] = useState(true);
  const [aircraftError,   setAircraftError]   = useState('');

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const pollRef    = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Aeronaves activas de la org ─────────────────────────────────
  const loadAircraft = useCallback(async () => {
    setAircraftError('');
    setAircraftLoading(true);
    try {
      const res = await api.get('/api/aircraft', { params: { is_active: 'true' } });
      const list = res?.data?.data || [];
      if (!mountedRef.current) return;
      setAircraftList(list);
    } catch (_err) {
      if (!mountedRef.current) return;
      setAircraftError(t('inventory_aircraft_load_error'));
      setAircraftList([]);
    } finally {
      if (mountedRef.current) setAircraftLoading(false);
    }
  }, [t]);

  useEffect(() => { loadAircraft(); }, [loadAircraft]);

  // ── Items para la aeronave seleccionada ─────────────────────────
  const loadItems = useCallback(async (showLoader = false) => {
    if (!selectedAircraftId) {
      setItems([]);
      setError('');
      setLoading(false);
      return;
    }
    if (showLoader) setLoading(true);
    setError('');
    try {
      const res = await inventoryService.list(buildParams(filters, selectedAircraftId));
      const data = res?.data?.data || [];
      if (!mountedRef.current) return;
      setItems(data);
    } catch (_err) {
      if (!mountedRef.current) return;
      setError(t('inventory_error_loading'));
    } finally {
      if (mountedRef.current && showLoader) setLoading(false);
    }
  }, [selectedAircraftId, filters, t]);

  // Carga inicial / al cambiar selección o filtros.
  useEffect(() => {
    if (!selectedAircraftId) {
      setItems([]);
      return;
    }
    loadItems(true);
  }, [selectedAircraftId, loadItems]);

  // Refresh externo (post-acción de modales)
  useEffect(() => {
    if (refreshKey > 0 && selectedAircraftId) loadItems(false);
    // eslint-disable-next-line
  }, [refreshKey]);

  // Polling — pausa si pestaña inactiva, sin aeronave o documento oculto.
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!isActive || !selectedAircraftId) return undefined;
    pollRef.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadItems(false);
    }, POLLING_INTERVALS.inventory);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [isActive, selectedAircraftId, loadItems]);

  const selectedAircraft = useMemo(
    () => aircraftList.find((a) => a.id === selectedAircraftId) || null,
    [aircraftList, selectedAircraftId],
  );

  // Conteo de "específicos vs universales" derivado del response actual.
  const { specificCount, universalCount } = useMemo(() => {
    let specific = 0;
    let universal = 0;
    items.forEach((it) => {
      if ((Number(it?.compatibilities_count) || 0) === 0) universal += 1;
      else specific += 1;
    });
    return { specificCount: specific, universalCount: universal };
  }, [items]);

  const isFiltered = !!(filters.search || filters.category || filters.tracking_mode || filters.low_stock);
  const handleClearFilters = () => setFilters(INITIAL_FILTERS);

  // ── Estilos ────────────────────────────────────────────────────
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
    color: fgPrimary,
    fontFamily: 'inherit',
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Selector de aeronave */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 10,
          alignItems: 'end',
        }}
      >
        <div>
          <label
            htmlFor="inventory-aircraft-select"
            style={{
              display: 'block', fontSize: 11, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              color: fgFaint, marginBottom: 6,
            }}
          >
            {t('inventory_aircraft_selector_label')}
          </label>
          <select
            id="inventory-aircraft-select"
            value={selectedAircraftId || ''}
            onChange={(e) => onAircraftChange?.(e.target.value || null)}
            disabled={aircraftLoading || !!aircraftError}
            style={inputStyle}
          >
            <option value="">{t('inventory_aircraft_selector_placeholder')}</option>
            {aircraftList.map((ac) => (
              <option key={ac.id} value={ac.id}>
                {ac.registration} — {ac.manufacturer ? `${ac.manufacturer} ` : ''}{ac.model_name || ''}
              </option>
            ))}
          </select>
          {aircraftError && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#dc2626' }}>
              {aircraftError}
            </p>
          )}
        </div>

        <motion.button
          type="button"
          onClick={() => { loadAircraft(); if (selectedAircraftId) loadItems(true); }}
          whileTap={{ scale: 0.96 }}
          aria-label={t('inventory_refresh')}
          title={t('inventory_refresh')}
          style={{
            padding: 11, borderRadius: 12, border: 'none', cursor: 'pointer',
            background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
            color: fgMuted, display: 'flex',
            alignSelf: 'end',
          }}
        >
          <RefreshCw style={{ width: 16, height: 16 }} />
        </motion.button>
      </div>

      {/* Sin aeronave seleccionada */}
      {!selectedAircraftId && !aircraftLoading && !aircraftError && (
        <EmptyState
          icon={Plane}
          title={t('inventory_aircraft_selector_empty_state_title')}
          desc={t('inventory_aircraft_selector_empty_state_desc')}
          darkMode={darkMode}
        />
      )}

      {/* Header de la aeronave seleccionada */}
      {selectedAircraft && (
        <div
          style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
            justifyContent: 'space-between',
            padding: '14px 16px', borderRadius: 16,
            background: darkMode ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.06)',
            border: darkMode ? '1px solid rgba(99,102,241,0.20)' : '1px solid rgba(99,102,241,0.18)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(79,70,229,0.28)',
                flexShrink: 0,
              }}
            >
              <Plane style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <p
                style={{
                  margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary,
                  letterSpacing: '-0.2px',
                }}
              >
                {t('inventory_aircraft_compatible_section_title', {
                  registration: selectedAircraft.registration,
                })}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: fgMuted }}>
                {selectedAircraft.manufacturer ? `${selectedAircraft.manufacturer} ` : ''}
                {selectedAircraft.model_name || ''}
                {(specificCount + universalCount) > 0 && (
                  <>
                    {' · '}
                    {t('inventory_aircraft_compatibility_summary', {
                      count: universalCount, // i18n plural agarra el `_one`/`_other`
                      specific: specificCount,
                      universal: universalCount,
                    })}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filtros + listado (solo si hay aeronave seleccionada) */}
      {selectedAircraftId && (
        <>
          <InventoryFiltersBar
            filters={filters}
            onChange={setFilters}
            darkMode={darkMode}
          />
          <InventoryItemsList
            items={items}
            loading={loading}
            error={error || null}
            groupBy="category"
            emptyVariant={isFiltered ? 'no_results' : 'no_items'}
            onClearFilters={isFiltered ? handleClearFilters : null}
            onRetry={() => loadItems(true)}
            darkMode={darkMode}
            locale={locale}
            onItemAction={onItemAction}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        </>
      )}
    </div>
  );
};

const EmptyState = ({ icon: Icon, title, desc, darkMode }) => {
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 14, padding: '48px 24px',
        borderRadius: 20,
        border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
        background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 56, height: 56, borderRadius: 18,
          background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon style={{ width: 26, height: 26, color: '#6366f1' }} />
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary }}>{title}</p>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: fgMuted, maxWidth: 460 }}>{desc}</p>
      </div>
    </div>
  );
};

export default ByAircraftView;
